import React, { useEffect, useState } from 'react';
import { setHeaderTitle } from 'features/ui/model/uiSlice';
import { useAppDispatch } from 'app/store';
import { useParams } from 'react-router-dom';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { StyledCard } from 'shared/ui/Card/StyledCard';
import { MoreInfo } from 'shared/ui/MoreInfo/MoreInfo';
import {
  useGetAdminAwardsQuery,
  useGetAdminCompetitionItemQuery,
  updateCompetitionStatusThunk,
  useGetAdminCompetitionItemParticipantsQuery,
} from 'entities/competitions-b2cr';
import { retrieveStatus } from 'shared/helpers/retriveStatus';
import { toast } from 'react-toastify';
import ParticipantsXlsxLoader from 'widgets/competitions-b2cr/ui/ParticipantsXlsxLoader';
import { useGetKPIListQuery } from 'features/getKpiList/api/getKpiListApi';
import { useGetBrandPlansListQuery } from 'features/getBrandPlans';
import { DataTableBackendPagination } from 'shared/ui/Table';
import DrawerCompetition from '../ui/DrawerCompetition';
import { competitionParticipantsColumnConfig } from './competitionParticipantsColumnConfig';
import { mapCompetitionParticipantsToRender } from './mapCompetitionParticipantsToRender';
import { CompetitionParticipantDeleteDrawer } from './CompetitionParticipantDeleteDrawer';

enum Status {
  Draft = 1,
  Planned = 2,
  Active = 3,
  Finished = 4,
  Canceled = 5,
}

interface RouteParams {
  id?: string;
  [key: string]: string | undefined;
}

export const CompetitionsDetail = () => {
  const params = useParams<RouteParams>();
  const id = params.id ? parseInt(params.id) : undefined;
  const [removeCompetitionParticipantId, setRemoveCompetitionParticipantId] = useState(-1);
  const [isDrawerRemoveParticipant, setIsDrawerRemoveParticipant] = useState(false);

  const [competitionParticipantsPagination, setCompetitionParticipantsPagination] = useState({
    rowsPerPage: 5,
    page: 0,
  });
  const dispatch = useAppDispatch();
  const { data: competitionData, refetch: refetchCompetitionData } = useGetAdminCompetitionItemQuery({
    competition_id: id,
  });
  const { data: competitionParticipants, refetch: refetchParticipants } = useGetAdminCompetitionItemParticipantsQuery({
    competition_id: id as number,
    page: competitionParticipantsPagination.page + 1,
    size: competitionParticipantsPagination.rowsPerPage,
  });
  const { data: kpiList } = useGetKPIListQuery({});
  const { data: brandPlansList } = useGetBrandPlansListQuery({});
  const { data: awards, refetch: refetchAwards } = useGetAdminAwardsQuery({
    competition_id: id as number,
  });
  const [status, setStatus] = useState<Status>(competitionData?.status || 1);

  const getDate = (value: string | undefined) => value && new Date(value).toLocaleDateString('ru');
  const data = {
    id,
    name: competitionData?.name,
    status: competitionData ? retrieveStatus(competitionData.status) : Status.Draft,
    kpi_name_ids: competitionData?.kpi_name_ids,
    brand_ids: competitionData?.brand_ids,
    date_award_end: getDate(competitionData?.date_award_end),
    date_award_start: getDate(competitionData?.date_award_start),
    date_end: getDate(competitionData?.date_end),
    date_start: getDate(competitionData?.date_start),
    participants_count: competitionData?.participants_count,
    date_current_rating: getDate(competitionData?.date_current_rating),
  };

  useEffect(() => {
    dispatch(setHeaderTitle(competitionData?.name || 'Соревнование'));
  }, [dispatch, competitionData]);

  useEffect(() => {
    if (competitionData?.status && competitionData.status !== status) {
      setStatus(competitionData.status);
    }
  }, [competitionData?.status]);

  const handleAddParticipant = () => {
    if (!competitionData?.participants_count && !competitionParticipants?.items.length) {
      toast.error('Добавьте в соревнование участников!');
      return false;
    }
    return true;
  };

  const handleAddAwards = (awardsData?: any[]) => {
    const awardsToCheck = awardsData || awards;
    if (!awardsToCheck || awardsToCheck.length === 0) {
      toast.error('Добавьте в соревнование призовые места!');
      return false;
    }
    return true;
  };

  const handleMinimumAwards = (awardsData?: any[]) => {
    const awardsToCheck = awardsData || awards;
    if (awardsToCheck && awardsToCheck.length < 3) {
      toast.error('Соревнование обязательно должно иметь 1, 2 и 3 призовые места');
      return false;
    }
    return true;
  };

  /**
   * @description Обработчик изменения статуса
   * @param newStatus
   */
  const handleOnChangeStatus = async (newStatus: Status) => {
    const oldStatus = status;
    const awardsResult = await refetchAwards();
    await refetchCompetitionData();

    if (!competitionData || !id || newStatus === competitionData?.status) return;

    if (oldStatus === Status.Draft) {
      if (!handleAddParticipant() || !handleAddAwards(awardsResult.data) || !handleMinimumAwards(awardsResult.data)) {
        return;
      }
    }

    dispatch(
      updateCompetitionStatusThunk({
        competition_id: id,
        status: newStatus,
      }),
    ).then((res) => {
      if (res.payload && (res.payload as { status: number }).status > 400) {
        setStatus(oldStatus);
      } else {
        setStatus(newStatus);
      }
    });
  };

  const renderKPIList = () => {
    if (!data || !data.kpi_name_ids?.length) {
      return '-';
    }

    const kpiNames: string[] = data.kpi_name_ids
      .map((item) => kpiList?.find((kpiItem) => kpiItem.id === item)?.name)
      .filter((name) => name !== undefined) as string[];

    return kpiNames.join(', ');
  };

  const renderBrandsList = () => {
    if (!data || !data.brand_ids?.length) {
      return '-';
    }
    const brandNames: string[] = data.brand_ids
      .map((item) => brandPlansList?.find((brandItem) => brandItem.id === item)?.name)
      .filter((name) => name !== undefined) as string[];
    return brandNames.join(', ');
  };

  return (
    <>
      <Box
        component={StyledCard}
        sx={{
          marginBottom: '24px',
        }}
      >
        <Box
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography>Детали соревнования</Typography>
          <Box
            style={{
              width: '30%',
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="select-label-type">Статус</InputLabel>
              <Select
                labelId="select-label-type"
                label="Статус"
                sx={{ marginBottom: '24px', width: '100%' }}
                value={status}
                onChange={(e) => handleOnChangeStatus(e.target.value as Status)}
              >
                <MenuItem value={1}>Черновик</MenuItem>
                <MenuItem value={2}>Запланированное</MenuItem>
                <MenuItem value={3}>Активное</MenuItem>
                <MenuItem value={4}>Завершенное</MenuItem>
                <MenuItem value={5}>Отмененное</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        {competitionData?.image_main ? (
          <img
            src={competitionData?.image_main}
            alt=""
            style={{ width: 64, height: 64 }}
          />
        ) : null}
        <Box
          style={{
            display: 'flex',
            marginTop: '12px',
            width: '100%',
          }}
        >
          <div
            className="column"
            style={{ width: '600px' }}
          >
            <MoreInfo>
              <div className="title">ID</div>
              <div className="content">{data?.id ?? '---'}</div>
            </MoreInfo>
            <MoreInfo>
              <div className="title">Статус</div>
              <div className="content">{data?.status ?? '---'}</div>
            </MoreInfo>
            {/* <MoreInfo>
                                <div className="title">Число участников</div>
                                <div className="content">{data?.participants_count ?? "0"}</div>
                            </MoreInfo> */}
            <MoreInfo>
              <div className="title">Число призовых мест</div>
              <div className="content">{awards?.length ?? '0'}</div>
            </MoreInfo>
            <MoreInfo>
              <div className="title">KPI</div>
              <div className="content">{renderKPIList()}</div>
            </MoreInfo>
            <MoreInfo>
              <div className="title">Бренды</div>
              <div className="content">{renderBrandsList()}</div>
            </MoreInfo>
          </div>
          <div
            className="column"
            style={{ width: '600px' }}
          >
            <MoreInfo>
              <div className="title">Период проведения соревнования</div>
              <div className="content">
                {data?.date_start ?? '---'} - {data?.date_end ?? '---'}
              </div>
            </MoreInfo>
            <MoreInfo>
              <div className="title">Период выдачи призов</div>
              <div className="content">
                {data?.date_award_start ?? '---'} - {data?.date_award_end ?? '---'}
              </div>
            </MoreInfo>
            <MoreInfo>
              <div className="title">Дата текущего рейтинга</div>
              <div className="content">{data?.date_current_rating ?? '---'}</div>
            </MoreInfo>
          </div>
        </Box>
        <div style={{ width: '100%' }}>
          <MoreInfo>
            <div className="title">Краткое описание</div>
            <div className="content">{competitionData?.description_short ?? '---'}</div>
          </MoreInfo>
          <MoreInfo>
            <div style={{ width: '100%' }}>
              <div className="title">Описание</div>
              <div
                className="content"
                style={{ wordBreak: 'normal' }}
              >
                {competitionData?.description ?? '---'}
              </div>
            </div>
          </MoreInfo>
        </div>
      </Box>
      <Box>
        <ParticipantsXlsxLoader
          competitionId={id as number}
          refetchParticipants={refetchParticipants}
        />
      </Box>
      {competitionParticipants ? (
        <DataTableBackendPagination
          title="Участники"
          data={mapCompetitionParticipantsToRender(competitionParticipants.items) as any}
          total={competitionParticipants?.total}
          pagination={competitionParticipantsPagination}
          setPagination={setCompetitionParticipantsPagination}
          columnConfig={competitionParticipantsColumnConfig}
          deleteIcon
          handleDrawerDelete={(id) => {
            setRemoveCompetitionParticipantId(id);
            setIsDrawerRemoveParticipant(true);
          }}
        />
      ) : null}
      <CompetitionParticipantDeleteDrawer
        competitionId={id as number}
        removeCompetitionParticipantId={removeCompetitionParticipantId}
        isShow={isDrawerRemoveParticipant}
        setIsShow={setIsDrawerRemoveParticipant}
        refetchParticipants={refetchParticipants}
      />
      <DrawerCompetition competitionId={id} />
    </>
  );
};
